import { Add as AddIcon } from "@mui/icons-material";
import {
  Alert,
  AlertColor,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  TaxesFeesModal,
  TaxesFeesTable,
} from "../../components/backend/taxesfees";
import {
  useTaxAndFeeMutations,
  useTaxAndFeeSettings,
} from "../../hooks/useTaxAndFee";
import { TaxAndFeeSetting } from "../../types/admin";

import AdminPage from "./AdminPage";

type ModalState = {
  open: boolean;
  mode: "create" | "edit";
  setting?: TaxAndFeeSetting;
};

const TaxesFeesPage: React.FC = () => {
  const { t } = useTranslation();

  const [confirmDelete, setConfirmDelete] = useState<TaxAndFeeSetting | null>(
    null
  );
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: AlertColor;
  }>({
    open: false,
    message: "",
    severity: "info",
  });
  const [modalState, setModalState] = useState<ModalState>({
    open: false,
    mode: "create",
  });

  const { data, isLoading } = useTaxAndFeeSettings();
  const { create, update, remove } = useTaxAndFeeMutations();

  const settings = useMemo(() => data?.data ?? [], [data]);

  const openCreateModal = () =>
    setModalState({ open: true, mode: "create", setting: undefined });

  const openEditModal = (setting: TaxAndFeeSetting) =>
    setModalState({ open: true, mode: "edit", setting });

  const closeModal = () =>
    setModalState((prev) => ({ ...prev, open: false, setting: undefined }));

  const showMessage = (message: string, severity: AlertColor = "info") =>
    setSnackbar({ open: true, message, severity });

  const handleSave = async (payload: {
    tax: number | null;
    fees: number | null;
    min: number;
    max: number;
    isActive: boolean;
    comment: string;
  }) => {
    try {
      if (modalState.mode === "create") {
        await create.mutateAsync(payload);
        showMessage(t("admin.taxesFees.created"), "success");
      } else if (modalState.setting?.id) {
        await update.mutateAsync({ id: modalState.setting.id, data: payload });
        showMessage(t("admin.taxesFees.updated"), "success");
      }
      closeModal();
    } catch (error) {
      console.error("Failed to save tax and fee setting", error);
      showMessage(t("admin.taxesFees.saveFailed"), "error");
    }
  };

  const handleDelete = async (setting: TaxAndFeeSetting) => {
    setConfirmDelete(setting);
  };

  const confirmDeletion = async () => {
    if (!confirmDelete) return;
    try {
      await remove.mutateAsync(confirmDelete.id);
      showMessage(t("admin.taxesFees.deleted"), "success");
    } catch (error) {
      console.error("Failed to delete product setting", error);
      showMessage(t("admin.taxesFees.deleteFailed"), "error");
    } finally {
      setConfirmDelete(null);
    }
  };

  return (
    <AdminPage>
      <Container maxWidth="xl">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 3,
          }}
        >
          <Box>
            <Typography variant="h4" gutterBottom>
              {t("admin.taxesFees.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("admin.taxesFees.subtitle")}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreateModal}
          >
            {t("common.add")}
          </Button>
        </Box>

        <TaxesFeesTable
          settings={settings}
          loading={
            isLoading ||
            create.isPending ||
            update.isPending ||
            remove.isPending
          }
          onEdit={openEditModal}
          onDelete={handleDelete}
        />

        <TaxesFeesModal
          open={modalState.open}
          mode={modalState.mode}
          initialData={modalState.setting}
          onClose={closeModal}
          onSave={handleSave}
          submitting={create.isPending || update.isPending}
        />

        <Dialog
          open={Boolean(confirmDelete)}
          onClose={() => setConfirmDelete(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>{t("admin.taxesFees.deleteTitle")}</DialogTitle>
          <DialogContent>
            {t("admin.taxesFees.deleteConfirmation")}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDelete(null)}>{t("common.cancel")}</Button>
            <Button
              color="error"
              variant="contained"
              onClick={confirmDeletion}
              disabled={remove.isPending}
            >
              {t("common.delete")}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </AdminPage>
  );
};

export default TaxesFeesPage;
